import { prepareDemoImportFiles } from '@blocksuite/affine-shared/utils';
import { assertCalendarIcsBytes } from '@streamlain/calendar-ics-security';

import { dataViewI18nT } from '../../../../core/i18n.js';
import { parseCalendarIcs } from '../../calendar-domain.js';
import type { CalendarSingleView } from '../../calendar-view-manager.js';
import type { CalendarIcsSubscription } from '../../define.js';
import type {
  CalendarEvent,
  CalendarSubscriptionState,
} from '../calendar-view-types.js';
import {
  createCalendarIcsSubscriptionFromUrl,
  createSignedCalendarFeedUrl,
  downloadCalendarIcs,
  fetchIcsSubscriptionEvents,
  renderCalendarEventsIcs,
} from './calendar-ics-service.js';
import {
  calendarReconcileColumns,
  flushCalendarReconcileDocs,
  resolveCalendarReconcileContext,
} from './calendar-reconcile-service.js';

type SetCellJson = (
  rowId: string,
  propertyId: string | undefined,
  value: unknown
) => void;

const subscriptionLoads = new WeakMap<
  CalendarIcsActionsContext,
  Map<string, number>
>();

export type CalendarIcsActionsContext = {
  signal: AbortSignal;
  addRow: () => string | undefined;
  closeFeedForm: () => void;
  getSubscriptionEvents: () => Readonly<Record<string, CalendarEvent[]>>;
  getSubscriptionLoadKey: () => string;
  getSubscriptionStates: () => Readonly<
    Record<string, CalendarSubscriptionState>
  >;
  readonly: () => boolean;
  requestCalendarUpdate: () => void;
  queueIntrinsicContentHeightChange: () => void;
  setCalendarError: (message: string | undefined) => void;
  setCellJson: SetCellJson;
  setSubscriptionEvents: (
    events: Readonly<Record<string, CalendarEvent[]>>
  ) => void;
  setSubscriptionLoadKey: (key: string) => void;
  setSubscriptionStates: (
    states: Readonly<Record<string, CalendarSubscriptionState>>
  ) => void;
  view: CalendarSingleView;
};

function setSubscriptionState(
  context: CalendarIcsActionsContext,
  id: string,
  state: CalendarSubscriptionState
): void {
  context.setSubscriptionStates({
    ...context.getSubscriptionStates(),
    [id]: state,
  });
}

export function ensureCalendarSubscriptionEventsLoaded(
  context: CalendarIcsActionsContext,
  subscriptions = context.view.calendarIcsSubscriptions$.value
): void {
  const loadKey = JSON.stringify(
    subscriptions.map(subscription => ({
      enabled: subscription.enabled !== false,
      id: subscription.id,
      url: subscription.url,
    }))
  );
  if (loadKey === context.getSubscriptionLoadKey()) return;
  context.setSubscriptionLoadKey(loadKey);
  const enabledIds = new Set(
    subscriptions
      .filter(subscription => subscription.enabled !== false)
      .map(subscription => subscription.id)
  );
  context.setSubscriptionEvents(
    Object.fromEntries(
      Object.entries(context.getSubscriptionEvents()).filter(([id]) =>
        enabledIds.has(id)
      )
    )
  );
  const subscriptionIds = new Set(
    subscriptions.map(subscription => subscription.id)
  );
  context.setSubscriptionStates(
    Object.fromEntries(
      Object.entries(context.getSubscriptionStates()).filter(([id]) =>
        subscriptionIds.has(id)
      )
    )
  );
  for (const subscription of subscriptions) {
    if (subscription.enabled === false) continue;
    void refreshCalendarIcsSubscription(context, subscription);
  }
}

export async function refreshCalendarIcsSubscription(
  context: CalendarIcsActionsContext,
  subscription: CalendarIcsSubscription
): Promise<void> {
  if (context.signal.aborted) return;
  const loads = subscriptionLoads.get(context) ?? new Map<string, number>();
  subscriptionLoads.set(context, loads);
  const generation = (loads.get(subscription.id) ?? 0) + 1;
  loads.set(subscription.id, generation);
  const current = () =>
    !context.signal.aborted &&
    loads.get(subscription.id) === generation &&
    context.view.calendarIcsSubscriptions$.value.some(
      item =>
        item.id === subscription.id &&
        item.url === subscription.url &&
        item.enabled !== false
    );
  setSubscriptionState(context, subscription.id, { loading: true });
  try {
    const events = await fetchIcsSubscriptionEvents(
      subscription,
      fetch,
      context.signal
    );
    if (!current()) return;
    context.setSubscriptionEvents({
      ...context.getSubscriptionEvents(),
      [subscription.id]: events,
    });
    setSubscriptionState(context, subscription.id, {
      loading: false,
      refreshedAt: new Date().toISOString(),
    });
    context.setCalendarError(undefined);
  } catch (error) {
    if (!current()) return;
    const message = dataViewI18nT('calendar.error.feedFailed');
    context.setSubscriptionEvents({
      ...context.getSubscriptionEvents(),
      [subscription.id]: [],
    });
    setSubscriptionState(context, subscription.id, {
      error: message,
      loading: false,
    });
    context.setCalendarError(
      dataViewI18nT('calendar.error.feedLoadFailed', {
        message,
        name: subscription.name || subscription.url,
      })
    );
  }
}

export function downloadCalendarEventIcs(
  context: CalendarIcsActionsContext,
  event: CalendarEvent
): void {
  try {
    downloadCalendarIcs(
      renderCalendarEventsIcs([event]),
      `${event.title || 'event'}.ics`
    );
    context.setCalendarError(undefined);
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.exportFailed')
    );
  }
}

export async function copyCalendarSubscriptionUrl(
  context: CalendarIcsActionsContext
): Promise<void> {
  if (context.readonly()) return;
  const reconcileContext = resolveCalendarReconcileContext(
    context.view.manager.dataSource
  );
  const columns = calendarReconcileColumns(context.view);
  if (!reconcileContext || !columns) {
    context.setCalendarError(dataViewI18nT('calendar.chrome.unavailable'));
    return;
  }
  try {
    const absoluteUrl = await createSignedCalendarFeedUrl({
      columns,
      context: reconcileContext,
      flushDocs: flushCalendarReconcileDocs,
      origin: window.location.origin,
      viewId: context.view.id,
    });
    if (context.signal.aborted || context.readonly()) return;
    if (!navigator.clipboard) throw new Error('Clipboard unavailable');
    await navigator.clipboard.writeText(absoluteUrl);
    context.setCalendarError(undefined);
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.feedUrlCreateFailed')
    );
  }
}

export function addCalendarIcsSubscriptionFromUrl(
  context: CalendarIcsActionsContext,
  url: string
): void {
  if (context.readonly()) return;
  try {
    const subscription = createCalendarIcsSubscriptionFromUrl(url);
    context.view.setCalendarIcsSubscriptions([
      ...context.view.calendarIcsSubscriptions$.value,
      subscription,
    ]);
    context.setSubscriptionLoadKey('');
    context.closeFeedForm();
    context.setCalendarError(undefined);
    context.requestCalendarUpdate();
    context.queueIntrinsicContentHeightChange();
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.feedInvalid')
    );
  }
}

export function removeCalendarIcsSubscription(
  context: CalendarIcsActionsContext,
  subscriptionId: string
): void {
  if (context.readonly()) return;
  context.view.setCalendarIcsSubscriptions(
    context.view.calendarIcsSubscriptions$.value.filter(
      subscription => subscription.id !== subscriptionId
    )
  );
  const { [subscriptionId]: _removedEvents, ...events } =
    context.getSubscriptionEvents();
  const { [subscriptionId]: _removedState, ...states } =
    context.getSubscriptionStates();
  context.setSubscriptionEvents(events);
  context.setSubscriptionStates(states);
  context.setSubscriptionLoadKey('');
  context.requestCalendarUpdate();
}

export function setCalendarIcsSubscriptionEnabled(
  context: CalendarIcsActionsContext,
  subscriptionId: string,
  enabled: boolean
): void {
  if (context.readonly()) return;
  context.view.setCalendarIcsSubscriptions(
    context.view.calendarIcsSubscriptions$.value.map(subscription =>
      subscription.id === subscriptionId
        ? { ...subscription, enabled }
        : subscription
    )
  );
  context.setSubscriptionLoadKey('');
  context.requestCalendarUpdate();
}

export async function importCalendarIcsFile(
  context: CalendarIcsActionsContext,
  change: Event
): Promise<void> {
  const input = change.currentTarget as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file || context.readonly()) return;
  return importCalendarIcsFileData(context, file);
}

export async function importCalendarIcsFileData(
  context: CalendarIcsActionsContext,
  file: File
): Promise<void> {
  if (context.readonly() || context.signal.aborted) return;
  try {
    const [prepared] = await prepareDemoImportFiles({
      entry: 'calendar-import',
      intent: 'calendar-ics',
      workspaceId: resolveCalendarReconcileContext(
        context.view.manager.dataSource
      )?.workspaceId,
      files: [file],
    });
    if (!prepared) throw new Error('Demo sample unavailable');
    assertCalendarIcsBytes(prepared.size);
    const text = await prepared.text();
    if (context.readonly() || context.signal.aborted) return;
    await importCalendarIcsText(context, text);
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.importFailed')
    );
  }
}

export async function importCalendarIcsText(
  context: CalendarIcsActionsContext,
  text: string
): Promise<void> {
  if (context.readonly() || context.signal.aborted) return;
  const view = context.view;
  if (
    !/^\s*BEGIN:VCALENDAR[\r\n]/i.test(text) ||
    !/[\r\n]END:VCALENDAR\s*$/i.test(text)
  ) {
    context.setCalendarError(dataViewI18nT('calendar.error.importFailed'));
    return;
  }
  let events: ReturnType<typeof parseCalendarIcs>;
  try {
    events = parseCalendarIcs(text);
  } catch (error) {
    context.setCalendarError(
      error instanceof Error
        ? error.message
        : dataViewI18nT('calendar.error.importFailed')
    );
    return;
  }
  const columns = await view.ensureCalendarColumnsLive();
  if (context.signal.aborted) return;
  for (const event of events) {
    const rowId = context.addRow();
    if (!rowId) continue;
    context.setCellJson(rowId, columns.titleColumn, event.title);
    context.setCellJson(rowId, columns.startDateCol, event.startAt.getTime());
    context.setCellJson(rowId, columns.endDateCol, event.endAt.getTime());
    context.setCellJson(rowId, columns.allDayCol, event.allDay);
    context.setCellJson(rowId, columns.timezoneCol, event.timezone);
    context.setCellJson(rowId, columns.calendarIdCol, event.calendarId);
    context.setCellJson(
      rowId,
      columns.locationPhysicalCol,
      event.locationPhysical
    );
    context.setCellJson(
      rowId,
      columns.locationVirtualCol,
      event.locationVirtual
    );
    context.setCellJson(rowId, columns.recurrenceCol, event.recurrence);
    context.setCellJson(
      rowId,
      columns.recurrenceExdateCol,
      event.recurrenceExdate
    );
    context.setCellJson(rowId, columns.recurrenceIdCol, event.recurrenceId);
    context.setCellJson(rowId, columns.attendeesCol, event.attendees);
    context.setCellJson(rowId, columns.descriptionCol, event.description);
    context.setCellJson(rowId, columns.statusCol, event.status);
  }
  context.setCalendarError(undefined);
}
